import React, { useState, useEffect } from "react";
import CommandPanel from "../../CommandPanel/CommandPanel";
import SuiteApi from "./../../../api/SuiteApi";
import RadioButton from "./../../Buttons/RadioButton";
import CheckBox from "./../../Buttons/CheckBox";
import PlateApi from "./../../../api/PlateApi";
import { PlateAddress } from "./../../../Model/PlateAddress";
import { CaptureProfileInfo } from "../../../Model/CaptureProfile";
import "./ImagePlateUsing.css";
import { useTypedSelector } from "../../../redux/reducers/RootReducer";
import { getModule } from "../../../redux/reducers/ModuleReducer";
import { EModuleType } from "../../../Model/EModuleType";
import {
  getModuleState,
  selectDetailModuleStateByType,
} from "../../../redux/reducers/ModuleStateReducer";
import { EImagingLevel } from "../../../Model/ImagingPreferences";
import MtiButton from "../../Buttons/MtiButton";
import { Plate } from "../../../Model/Plate";
import { ELimsConnectionStatus } from "../../../Model/ModuleState";

const DROP = "DROP";
const INTERESTING_DROP = "INTERESTING_DROP";
const PLATE = "PLATE";
const DEFAULT_SETTINGS = "DEFAULT_SETTINGS";
const CUSTOM_SETTINGS = "CUSTOM_SETTINGS";

type Props = {
  plateAddresses: PlateAddress[];
  back: () => void;
  close: () => void;
  showImageDrop: boolean;
  style?: React.CSSProperties;
};

type Option = {
  captureProfileIds: string[];
  imagerIdentifier: string | null;
  level: EImagingLevel;
};

const initialOption: Option = {
  captureProfileIds: [],
  imagerIdentifier: null,
  level: EImagingLevel.Plate,
};

export default function ImagePlateUsing({
  plateAddresses,
  back,
  close,
  showImageDrop,
  style,
}: Props) {
  const { fis: fisState } = useTypedSelector((state) =>
    selectDetailModuleStateByType(state, EModuleType.FIS)
  );
  const isFisConnected = fisState?.state === ELimsConnectionStatus.Connected;

  const module = useTypedSelector((state) =>
    getModule(state, plateAddresses[0].moduleIdentifier)
  );
  const moduleState = useTypedSelector((state) =>
    getModuleState(state, module.identifier)
  );
  const imagerState = moduleState.imager;
  const [captureProfileInfos, setCaptureProfileInfos] = useState(
    [] as CaptureProfileInfo[]
  );
  const [haveCaptureProfiles, setHaveCaptureProfiles] = useState(false);
  const [option, setOption] = useState(initialOption);
  const [captureProfileSettings, setCaptureProfileSettings] =
    useState(DEFAULT_SETTINGS);

  const plates: Plate[] = plateAddresses
    .map((pa) => pa.usedByPlate ?? pa.reservedByPlate)
    .filter((pa) => pa !== undefined) as Plate[];

  useEffect(() => {
    if (isFisConnected) {
      SuiteApi.getCaptureProfileInfos().then((captureProfileInfos) => {
        setCaptureProfileInfos(captureProfileInfos);
        setHaveCaptureProfiles(captureProfileInfos.length > 0);
      });
    }
  }, [isFisConnected]);

  if (captureProfileInfos.length === 0) {
    return (
      <CommandPanel>
        <CommandPanel.TitleHeader title="Loading capture profiles" />
        {!haveCaptureProfiles && (
          <CommandPanel.VerticalContent>
            <div className="image-plate-using-h1">
              <span className="image-plate-using-title">
                No capture profiles defined
              </span>
              <svg
                className="image-plate-using-back-btn"
                onClick={back}
                width="48"
                height="48"
                viewBox="0 0 48 48"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M24 29.1L35.9 41L41 35.9L29.1 24L41 12.1L35.9 7L24 18.9L12.1 7L7 12.1L18.9 24L7 35.9L12.1 41L24 29.1Z"
                  fill="#8C2228"
                />
              </svg>
            </div>
          </CommandPanel.VerticalContent>
        )}
      </CommandPanel>
    );
  }

  const onLevelChange = (imagingLevel: EImagingLevel) => {
    setOption({
      ...option,
      level: imagingLevel,
    });
  };

  const onCaptureProfileSettingsClicked = (id: string) => {
    setCaptureProfileSettings(id);
    if (id === DEFAULT_SETTINGS) {
      setOption({
        ...option,
        captureProfileIds: [],
      });
    }
  };

  const onCaptureProfileClicked = (id: string, checked: boolean) => {
    const captureProfileId = id;
    if (checked) {
      const newCaptureProfileIds = [
        ...option.captureProfileIds,
        captureProfileId,
      ];
      setOption({
        ...option,
        captureProfileIds: newCaptureProfileIds,
      });
    } else {
      const newCaptureProfileIds = option.captureProfileIds.filter(
        (id) => id !== captureProfileId
      );
      setOption({
        ...option,
        captureProfileIds: newCaptureProfileIds,
      });
    }
  };

  const onClickApply = () => {
    for (let plate of plates) {
      PlateApi.imagePlate(
        plate.barcode,
        {
          imagingLevel: option.level,
          dropNumber:
            option.level === EImagingLevel.Drop
              ? imagerState?.dropNumber ?? 0
              : 0,
          wellNumber:
            option.level === EImagingLevel.Drop
              ? imagerState?.wellNumber ?? 0
              : 0,
          requestedCaptureProfileIds: option.captureProfileIds,
        },
        ""
      );
    }
    close();
  };

  const captureProfileViews = captureProfileInfos.map((captureProfile) => {
    return (
      <CheckBox
        id={captureProfile.captureProfileId}
        label={captureProfile.name}
        name={captureProfile.name}
        checked={option.captureProfileIds.includes(
          captureProfile.captureProfileId
        )}
        disabled={captureProfileSettings === DEFAULT_SETTINGS}
        style={{
          height: 40,
          fontSize: "20px",
        }}
        onChange={onCaptureProfileClicked}
      />
    );
  });

  const okButtonDisabled =
    captureProfileSettings === CUSTOM_SETTINGS &&
    option.captureProfileIds.length === 0;

  return (
    <CommandPanel
      style={{
        position: "relative",
        ...style,
      }}
    >
      <CommandPanel.PlateInfoHeader plateAddresses={plateAddresses} />

      <CommandPanel.VerticalContent>
        <div className="image-plate-using-h1">
          <span className="image-plate-using-title">
            Select Level and Imaging Settings
          </span>
          <svg
            className="image-plate-using-back-btn"
            onClick={back}
            width="48"
            height="48"
            viewBox="0 0 48 48"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M24 29.1L35.9 41L41 35.9L29.1 24L41 12.1L35.9 7L24 18.9L12.1 7L7 12.1L18.9 24L7 35.9L12.1 41L24 29.1Z"
              fill="#8C2228"
            />
          </svg>
        </div>
        <div className="image-plate-using-body">
          <span className="image-plate-using-h2">Level</span>
          <div className="image-plate-using-list">
            {module.moduleType === EModuleType.IMAGER && showImageDrop && (
              <RadioButton
                id={DROP}
                label="Current Drop"
                name={DROP}
                checked={option.level === EImagingLevel.Drop}
                onChange={() => onLevelChange(EImagingLevel.Drop)}
              />
            )}

            <RadioButton
              id={DROP}
              label="Interesting Drop(s)"
              name={INTERESTING_DROP}
              checked={option.level === EImagingLevel.InterestingDrop}
              onChange={() => onLevelChange(EImagingLevel.InterestingDrop)}
            />

            <RadioButton
              id={PLATE}
              label="Plate(s)"
              name={PLATE}
              checked={option.level === EImagingLevel.Plate}
              onChange={() => onLevelChange(EImagingLevel.Plate)}
            />
          </div>

          <span className="image-plate-using-h2">Imaging Settings</span>

          <div className="image-plate-using-list">
            <RadioButton
              id={DEFAULT_SETTINGS}
              label="Default Settings"
              name={DEFAULT_SETTINGS}
              checked={captureProfileSettings === DEFAULT_SETTINGS}
              onChange={() => onCaptureProfileSettingsClicked(DEFAULT_SETTINGS)}
            />
            <RadioButton
              id={CUSTOM_SETTINGS}
              label="Custom Settings"
              name={CUSTOM_SETTINGS}
              checked={captureProfileSettings === CUSTOM_SETTINGS}
              onChange={() => onCaptureProfileSettingsClicked(CUSTOM_SETTINGS)}
            />
            <div className="image-plate-using-sub-list">
              {captureProfileViews}
            </div>
          </div>
        </div>
        <MtiButton
          size="large"
          style={{
            width: 200,
            height: 44,
            fontSize: "18px",
            position: "absolute",
            right: "5%",
            bottom: "2.5%",
          }}
          onClick={onClickApply}
          disabled={okButtonDisabled}
        >
          OK
        </MtiButton>
      </CommandPanel.VerticalContent>
    </CommandPanel>
  );
}
